import React from "react";
import { Divider, Typography, theme } from "antd";
import { ProCard } from "@ant-design/pro-components";
import { CaretDownFilled, CaretUpFilled } from "@ant-design/icons";

import styles from "./index.module.scss";

interface MetricCardLink {
  label: string;
  to: string;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  trend?: string;
  trendPositive?: boolean;
  description?: string;
  link?: MetricCardLink;
}

export default function MetricCard({
  title,
  value,
  trend,
  trendPositive,
  description,
  link,
}: MetricCardProps) {
  const {
    token: { colorSuccess, colorError },
  } = theme.useToken();

  return (
    // The shared card rule spaces every child 16px apart. Here the caption
    // under the figure belongs to it and sits closer, so the gap drops to 8 and
    // the figure row buys its 16 back from the title above it. Passed as a
    // style rather than a rule, so it does not have to outrank the shared one.
    <ProCard className={styles.card} bodyStyle={{ gap: 8 }}>
      <Typography.Text type="secondary">{title}</Typography.Text>

      <div className={styles.figure}>
        <div>{value}</div>

        {trend && (
          <div
            className={styles.trend}
            style={{
              // The theme's own words for up and down; the literals here were
              // colorSuccess and colorError spelled out.
              color: trendPositive ? colorSuccess : colorError,
            }}
          >
            {trendPositive ? <CaretUpFilled /> : <CaretDownFilled />}
            {trend}
          </div>
        )}
      </div>

      {(description || link) && <Divider className={styles.divider} />}

      {description && <Typography.Text>{description}</Typography.Text>}

      {link && (
        <div>
          <a href={link.to}>{link.label}</a>
        </div>
      )}
    </ProCard>
  );
}
